import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';

import { GlobalButtonComponent } from '@shared/components/global-button/global-button.component';
import { GlobalPopupComponent } from '@shared/components/global-popup/global-popup.component';
import { GeneralAuth } from '@services/auth/generalAuth/generalAuth.service';
import { LoginService } from '@services/auth/login.service';
import { TranslationsService } from '@services/translate/translations.service';

/** Tiempo tras el cual se cierra la sesión automáticamente si el usuario no responde, en milisegundos. */
const AUTO_LOGOUT_MS = 5000;

/**
 * Dialog de sesión expirada. Se muestra cuando `GeneralAuth` no logra renovar el token
 * y cierra la sesión al aceptar, al pulsar Escape/clic fuera o tras 5 segundos.
 */
@Component({
  selector: 'app-session-expired-dialog',
  imports: [GlobalPopupComponent, GlobalButtonComponent],
  templateUrl: './session-expired-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionExpiredDialogComponent {
  private readonly _generalAuth = inject(GeneralAuth);
  private readonly _loginService = inject(LoginService);
  private readonly _translations = inject(TranslationsService);
  private readonly _msgSignal = this._translations.sectionSignal('message_system');
  private readonly _btnSignal = this._translations.sectionSignal('button');

  /** Indica si el dialog debe mostrarse. */
  readonly visible = this._generalAuth.sessionExpiredDialog;

  /**
   * Resuelve una clave de la sección `message_system`.
   * @param key - Clave de traducción
   * @param fallback - Texto de respaldo si la clave no existe
   * @returns Texto traducido o el respaldo
   */
  readonly tMsg = (key: string, fallback: string): string =>
    this._msgSignal()?.[key] ?? this._msgSignal()?.gene_code_tran_code_keys?.[key] ?? fallback;

  /**
   * Resuelve una clave de la sección `button`.
   * @param key - Clave de traducción
   * @param fallback - Texto de respaldo si la clave no existe
   * @returns Texto traducido o el respaldo
   */
  readonly tBtn = (key: string, fallback: string): string =>
    this._btnSignal()?.[key] ?? this._btnSignal()?.gene_code_tran_code_keys?.[key] ?? fallback;

  constructor() {
    effect((onCleanup) => {
      if (!this.visible()) return;
      const handle = setTimeout(() => this.accept(), AUTO_LOGOUT_MS);
      onCleanup(() => clearTimeout(handle));
    });
  }

  /** Oculta el dialog y cierra la sesión redirigiendo al login. */
  accept(): void {
    if (!this.visible()) return;
    this._generalAuth.sessionExpiredDialog.set(false);
    this._loginService.logOut();
  }
}
